import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Spinner } from './Spinner.js';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * In-flight state. Every ⟳ command carries an Idempotency-Key, so a retry is
   * safe — the button disables to avoid a pointless second request, never to
   * protect against a double charge.
   */
  loading?: boolean;
  children: ReactNode;
}

/**
 * The button's visual identity, separated from the `<button>` element.
 *
 * A navigation control must be an `<a>`: putting a `<button>` inside a `<Link>`
 * is interactive content nested in a link, which produces two tab stops for one
 * control and gives assistive technology two conflicting roles. Route CTAs
 * therefore render a `Link` carrying this class rather than wrapping a Button.
 */
export function buttonClassName(
  variant: ButtonVariant = 'secondary',
  size: ButtonSize = 'md',
): string {
  return `btn btn--${variant} btn--${size}`;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      {...rest}
      type={type}
      className={buttonClassName(variant, size)}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
    >
      {loading ? <Spinner size={size === 'lg' ? 16 : 14} /> : null}
      {children}
    </button>
  );
}
